import { Box, ButtonBase, Card, Popover, Typography } from "@mui/material";
import { MouseEvent, useState } from "react";
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { GqlMapShapeIcon } from "~/__generated__";

import { MAP_SHAPE_ICON_FILES } from "../../mapShapeIcons";

const SHAPE_OPTIONS = Object.values(GqlMapShapeIcon);

type FieldProps = {
    value: GqlMapShapeIcon;
    onChange: (value: GqlMapShapeIcon) => void;
    label?: string;
};

function MapShapeIconPickerField({ value, onChange, label }: FieldProps) {
    const { t } = useTranslation("map");
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleSelect = (shape: GqlMapShapeIcon) => {
        onChange(shape);
        handleClose();
    };

    return (
        <Box>
            {label && (
                <Typography variant="caption" color="text.secondary" component="div">
                    {label}
                </Typography>
            )}

            <Card
                component={ButtonBase}
                elevation={0}
                onClick={handleClick}
                sx={{
                    padding: 1,
                    border: "solid 2px",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 20,
                }}
            >
                <Box component="img" src={MAP_SHAPE_ICON_FILES[value]} alt={value} sx={{ width: 24, height: 24 }} />
                <Typography>{t(`mapShapeIcon.${value}`)}</Typography>
            </Card>

            <Popover
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 1fr)",
                        gap: 1,
                        padding: 2,
                    }}
                >
                    {SHAPE_OPTIONS.map(shape => (
                        <ButtonBase
                            key={shape}
                            onClick={() => handleSelect(shape)}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 0.5,
                                p: 1,
                                borderRadius: 1,
                                border: "solid 2px",
                                borderColor: shape === value ? "primary.main" : "transparent",
                            }}
                        >
                            <Box component="img" src={MAP_SHAPE_ICON_FILES[shape]} alt={shape} sx={{ width: 28, height: 28 }} />
                            <Typography variant="caption" noWrap>
                                {t(`mapShapeIcon.${shape}`)}
                            </Typography>
                        </ButtonBase>
                    ))}
                </Box>
            </Popover>
        </Box>
    );
}

type Props<T extends FieldValues> = {
    control: Control<T>;
    name: FieldPath<T>;
    label?: string;
};

export default function MapShapeIconPicker<T extends FieldValues>({ control, name, label }: Props<T>) {
    return (
        <Controller
            control={control}
            name={name}
            render={({ field }) => (
                <MapShapeIconPickerField value={field.value as GqlMapShapeIcon} onChange={field.onChange} label={label} />
            )}
        />
    );
}
